<template>
    <div class="columns">
        <div class="column is-8">
            <div class="is-size-4">
                <span class="has-text-grey-light">{{ translate("common.competitor") }}:</span>
                <span class="clarity-mask">{{ selectedCompetitorPayment.firstName }} {{ selectedCompetitorPayment.lastName }}</span>
                <span class="has-text-grey is-size-6">#{{ selectedCompetitorPayment.id }}</span>
            </div>
            <div class="is-size-4">
                <span class="has-text-grey-light">{{ translate("common.category") }}:</span>
                {{ selectedCompetitorPayment.category }}
            </div>
            <div class="is-size-4">
                <span class="has-text-grey-light">{{ translate("common.date") }}:</span>
                <RegistrationStageNumber
                    :stage-number="selectedCompetitorPayment.stageNumber"
                    :date="selectedCompetitorPayment.creationDate"
                    :timezone="eventTimezone"
                />
            </div>

            <div class="mt-3">
                <button
                    class="button is-light"
                    :disabled="!selectedCompetitorPayment.paymentDocument"
                    @click="onOpenPaymentDocument(selectedCompetitorPayment.paymentDocument?.id ?? 0)"
                >
                    <span class="icon">
                        <i class="fa fa-file-invoice-dollar"></i>
                    </span>
                    <span>
                        {{ translate("admin_payments.payment_document_files") }}
                    </span>
                </button>
            </div>
        </div>

        <div class="selected-competitor-amount column is-4" v-if="requestsInProgress === 0">
            <template v-if="editSelectedCompetitor">
                <div class="field">
                    <div class="control">
                        <input class="input" type="text" v-model="amount" />
                    </div>
                </div>
                <div class="field">
                    <div class="control">
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="isComplete" />
                                {{ translate("admin_payment.payment_complete") }}
                            </label>
                        </div>
                    </div>
                </div>
                <div class="field">
                    <div class="buttons">
                        <button class="button is-success" @click="confirmEditSelectedCompetitor" :disabled="amountIsValidValue">
                            {{ translate("common.ok") }}
                        </button>
                        <button class="button is-danger" @click="cancelEditSelectedCompetitor">
                            {{ translate("common.cancel") }}
                        </button>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="field">
                    <label class="label">{{ translate("admin_payments.payment_status") }}:</label>
                    <div class="control">
                        <CompetitorPaymentStatus :competitor="selectedCompetitorPayment" :show-none-payment="true" class="are-medium" />
                    </div>
                </div>
                <div>
                    <template v-if="selectedCompetitorPayment.payment.status === 'ONLINE_COMPLETE'">
                        <button class="button is-warning" @click="detachOnlinePayment">
                            <span class="icon">
                                <i class="fas fa-exclamation-triangle"></i>
                            </span>
                            <span>{{ translate("admin_payment.detach_online_payment") }}</span>
                        </button>
                        <p class="has-text-warning mt-2">
                            {{ translate("admin_payment.detach_online_payment_warning") }}
                        </p>
                    </template>
                    <button v-else class="button is-primary" @click="startEditSelectedCompetitor" :disabled="!changeIsAllowed">
                        {{ translate("common.change") }}
                    </button>
                </div>
            </template>
        </div>
        <div class="column is-4" v-else-if="requestsInProgress > 0">
            <LoadingSpinner2 class="has-added-margin" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import CompetitorPaymentStatus from "./CompetitorPaymentStatus.vue";
    import RegistrationStageNumber from "./RegistrationStageNumber.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import { translate } from "../../Common/translations";
    import { eventBus, OPEN_PAYMENT_DOCUMENT } from "../eventBus";
    import { PAYMENT_STATUS_ONLINE_COMPLETE, PAYMENT_STATUS_ONLINE_PROCESSING } from "../../Common/consts";
    import paymentsApi, { CompetitorPaymentWithMetadata } from "../../Api/payments";
    import { successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";
    import urls from "../../Urls/urls";

    const props = defineProps<{
        selectedCompetitorPayment: CompetitorPaymentWithMetadata;
        eventId: number;
        eventTimezone: string;
    }>();

    const emit = defineEmits(["selectedCompetitorUpdated"]);

    const editSelectedCompetitor = ref(false);
    const amount = ref(0);
    const isComplete = ref(false);
    const requestsInProgress = ref(0);

    const amountIsValidValue = computed(() => {
        const amountStr = "" + amount.value;
        return amountStr.match(/^[0-9]+$/) === null;
    });

    const changeIsAllowed = computed(() => {
        const status = props.selectedCompetitorPayment.payment.status;
        return status !== PAYMENT_STATUS_ONLINE_PROCESSING && status !== PAYMENT_STATUS_ONLINE_COMPLETE;
    });

    function startEditSelectedCompetitor() {
        editSelectedCompetitor.value = true;
        amount.value = props.selectedCompetitorPayment.payment.amount;
        isComplete.value = props.selectedCompetitorPayment.payment.is_complete;
    }

    function detachOnlinePayment(): Promise<void> {
        if (!confirm(translate("common.are_you_sure"))) {
            return Promise.resolve();
        }

        return paymentsApi
            .detachOnlinePayment(props.eventId, props.selectedCompetitorPayment.id)
            .then(() => {
                return paymentsApi.getSingleCompetitorPayment(
                    props.eventId,
                    props.selectedCompetitorPayment.id,
                    props.selectedCompetitorPayment.userId,
                );
            })
            .then(updatedCompetitorPayment => {
                emit("selectedCompetitorUpdated", updatedCompetitorPayment);
                successToast(translate("admin_payments.competitor_updated"));
            })
            .catch(handleError);
    }

    function confirmEditSelectedCompetitor() {
        return paymentsApi
            .updateCompetitorPayment(props.eventId, props.selectedCompetitorPayment.id, amount.value, isComplete.value)
            .then(() => {
                return paymentsApi.getSingleCompetitorPayment(
                    props.eventId,
                    props.selectedCompetitorPayment.id,
                    props.selectedCompetitorPayment.userId,
                );
            })
            .then(updatedCompetitorPayment => {
                emit("selectedCompetitorUpdated", updatedCompetitorPayment);
                successToast(translate("admin_payments.competitor_updated"));
            })
            .catch(handleError)
            .finally(() => {
                editSelectedCompetitor.value = false;
                amount.value = 0;
                isComplete.value = false;
            });
    }

    function cancelEditSelectedCompetitor() {
        amount.value = 0;
        isComplete.value = false;
        editSelectedCompetitor.value = false;
    }

    function onOpenPaymentDocument(paymentDocumentId: number) {
        const url = urls.userEventPagesUrls().paymentDocumentDownload(props.eventId, paymentDocumentId, false);
        eventBus.$emit(OPEN_PAYMENT_DOCUMENT, url);
    }
</script>

<style scoped></style>
